import { useMemo } from 'react'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useGroupedLoyaltyEarnTotals, {
  type GroupedLoyaltyEarnCalculationRequest,
  type LoyaltyEarnTotals,
} from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import {
  generateLoyaltyPointsCalculatorBedbankOptions,
  generateLoyaltyPointsCalculatorBundledFlightOptions,
  generateLoyaltyPointsCalculatorHotelOptions,
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isLEHotel } from 'lib/offer/offerTypes'
import { getIsFlightsSelected } from 'selectors/offerPage/offerPageSelectors'
import type { PropertyRoomRateViewForAssistant } from './offerPageContextTypes'
import { getBedbankMemberTotalIncludingPropertyFees } from './offerPageBedbankPricing'

interface UseOfferPageRoomPackageLoyaltyEarnTotalsParams {
  offerPageState: OfferPageState
  currentRegionCode: string
  hotelPackageViews: Array<App.OfferPackageOptionView>
  bedbankPackageViews: Array<App.BedbankOfferPackageView>
  propertyRoomRateViews: Array<PropertyRoomRateViewForAssistant>
  propertyIsUltraLux: boolean
  /**
   * The real, live flight price (`getEffectiveHotelFlightPrice`) -- not `packageView.flightPrice`,
   * a static calendar-level indicative sample fare the real page stops using entirely once real
   * dates are picked. Flash-only; `undefined`/0 means no flight-bundle points should be added.
   */
  effectiveFlightPrice?: number
  /**
   * Mirrors `FlashHotelOptionPriceDetails.tsx`'s own `hideHotelAndFlightsPriceBlock` charter
   * condition: true when a charter bundle is using fallback (no matching check-in date) pricing.
   * The real page skips its flight-bundle earn request in this state -- there's no live per-date
   * flight fare to earn cash points against -- showing hotel-only earn instead. Flash-only.
   */
  shouldUseCharterFallbackPricing?: boolean
  /**
   * Mirrors `FlashHotelOptionPriceDetails.tsx`'s own `isRewardSelected` (`rewardAvailable &&
   * rewardSeatPointsTotal != null`): true when the customer is currently paying for the charter
   * flight seat itself with Société points rather than cash. The real page skips its
   * flight-bundle earn request in this state too -- there's no cash spend on that leg to earn
   * points against. Flash-only.
   */
  isRewardSelected?: boolean
  /**
   * Backend-resolved bonus-earn campaign key for the current flight (`useOfferPageFlightPrice`'s
   * `loyaltyEarnKey`, sourced from the journey/fare-family data) -- without it, a route-specific
   * earn bonus the real page's own flight-bundle request applies is silently dropped here.
   * Flash-only.
   */
  flightLoyaltyEarnKey?: string
}

// Rate/package identity, not array position -- a room can have a variable number of rates, so a
// stable string key (rather than an index into some particular array) is what lets
// `buildOfferPageSnapshot.ts` look up the right entry for a specific rate without needing to
// independently re-derive this file's exact iteration order.
type OfferPageRoomPackageLoyaltyGroupKey = string
type OfferPageRoomPackageLoyaltyCalculationRequest = GroupedLoyaltyEarnCalculationRequest<OfferPageRoomPackageLoyaltyGroupKey>

function addCalculationRequest(
  calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest>,
  groupKey: OfferPageRoomPackageLoyaltyGroupKey,
  request: App.LoyaltyPointsEarnCalculationRequest | undefined,
) {
  if (!request) {
    return
  }

  calculationRequests.push({
    groupKey,
    request,
  })
}

function buildPropertyLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  propertyRoomRateViews,
  propertyIsUltraLux,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'propertyRoomRateViews' | 'propertyIsUltraLux'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const property = offerPageState.property
  if (!property || propertyRoomRateViews.length === 0) {
    return []
  }

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  propertyRoomRateViews.forEach((roomRateView) => {
    roomRateView.rates.forEach((rate) => {
      addCalculationRequest(
        calculationRequests,
        rate.rateId,
        generateLoyaltyPointsCalculatorPropertyOptions(rate, {
          propertyCountryCode: property.location.countryCode,
          propertyStateProvince: property.location.region,
          currentRegionCode,
          isUltraLux: propertyIsUltraLux,
          nights: offerPageState.duration,
        }),
      )
      addCalculationRequest(
        calculationRequests,
        rate.rateId,
        generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
      )
    })
  })

  return calculationRequests
}

function buildHotelLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  hotelPackageViews,
  effectiveFlightPrice,
  shouldUseCharterFallbackPricing,
  isRewardSelected,
  flightLoyaltyEarnKey,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'hotelPackageViews' | 'effectiveFlightPrice' | 'shouldUseCharterFallbackPricing' | 'isRewardSelected' | 'flightLoyaltyEarnKey'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const offer = offerPageState.offer
  if (!isLEHotel(offer) || hotelPackageViews.length === 0) {
    return []
  }

  // Match the real page's own gate (`FlashHotelOptionPriceDetails.tsx`'s `isFlightsSelected`) so a
  // hotel-only booking with a leftover/geo-defaulted airport doesn't get flight-bundle points
  // added on top.
  const isFlightsSelected = getIsFlightsSelected(offerPageState)

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  hotelPackageViews.forEach((packageView) => {
    const groupKey = packageView.package.uniqueKey
    addCalculationRequest(
      calculationRequests,
      groupKey,
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        packageView,
        currentRegionCode,
        nights: packageView.package.duration,
      }),
    )
    addCalculationRequest(
      calculationRequests,
      groupKey,
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        packageView,
        flightPrice: effectiveFlightPrice && effectiveFlightPrice > 0 ? effectiveFlightPrice : undefined,
        loyaltyEarnKey: flightLoyaltyEarnKey,
        skip: !effectiveFlightPrice || effectiveFlightPrice <= 0 || !isFlightsSelected ||
          !!shouldUseCharterFallbackPricing || !!isRewardSelected,
      }),
    )
  })

  return calculationRequests
}

function buildBedbankLoyaltyCalculationRequests({
  offerPageState,
  currentRegionCode,
  bedbankPackageViews,
}: Pick<UseOfferPageRoomPackageLoyaltyEarnTotalsParams, 'offerPageState' | 'currentRegionCode' | 'bedbankPackageViews'>): Array<OfferPageRoomPackageLoyaltyCalculationRequest> {
  const offer = offerPageState.bedbankOffer
  if (!offer || bedbankPackageViews.length === 0) {
    return []
  }

  const calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest> = []
  bedbankPackageViews.forEach((packageView) => {
    // `effectiveRates`, not `rates` -- must match the snapshot builder's own rate set (and rate
    // IDs) exactly, since `buildOfferPageSnapshot.ts` looks up these totals by rate ID.
    packageView.effectiveRates.forEach((rate) => {
      addCalculationRequest(
        calculationRequests,
        rate.id,
        generateLoyaltyPointsCalculatorBedbankOptions(offer, rate, {
          price: rate.totals.total,
          memberPrice: getBedbankMemberTotalIncludingPropertyFees(rate),
          propertyFees: rate.totals.propertyFees ?? 0,
          currentRegionCode,
          nights: rate.nights,
        }),
      )
    })
  })

  return calculationRequests
}

function buildCalculationRequests(
  params: UseOfferPageRoomPackageLoyaltyEarnTotalsParams,
): {
  groupKeys: Array<OfferPageRoomPackageLoyaltyGroupKey>
  calculationRequests: Array<OfferPageRoomPackageLoyaltyCalculationRequest>
} {
  if (params.propertyRoomRateViews.length > 0) {
    return {
      groupKeys: params.propertyRoomRateViews.flatMap(roomRateView => roomRateView.rates.map(rate => rate.rateId)),
      calculationRequests: buildPropertyLoyaltyCalculationRequests(params),
    }
  }

  if (params.hotelPackageViews.length > 0) {
    return {
      groupKeys: params.hotelPackageViews.map(packageView => packageView.package.uniqueKey),
      calculationRequests: buildHotelLoyaltyCalculationRequests(params),
    }
  }

  return {
    groupKeys: params.bedbankPackageViews.flatMap(packageView => packageView.effectiveRates.map(rate => rate.id)),
    calculationRequests: buildBedbankLoyaltyCalculationRequests(params),
  }
}

export type OfferPageLoyaltyEarnCalculationStatus = 'loading' | 'partial' | 'unavailable' | 'ready'

/**
 * Aggregate status across every calculation in the batch, mirroring `ratesStatusFact`'s
 * loading/unavailable-or-nothing shape: `undefined` when nothing has been requested yet (no fact
 * to report), `'loading'` while any calculation is still in flight, `'unavailable'` once settled
 * with every calculation errored, `'partial'` once settled with a mix of data and errors, and
 * `'ready'` once settled with no errors at all. Computed here (not in `useGroupedLoyaltyEarnTotals`,
 * which is `src/loyalty`-owned) from a second, tripPlanner-owned read of the same underlying
 * calculations -- see the `pointsCalculations` hook call below for why this doesn't duplicate work.
 */
export function getOfferPageLoyaltyEarnCalculationStatus(
  pointsCalculations: Array<App.StatefulData<App.LoyaltyPointsEarnCalculation> | undefined>,
): OfferPageLoyaltyEarnCalculationStatus | undefined {
  if (pointsCalculations.length === 0) {
    return undefined
  }

  if (pointsCalculations.some(calculation => !calculation || calculation.initial || calculation.fetching)) {
    return 'loading'
  }

  const hasError = pointsCalculations.some(calculation => calculation?.error !== undefined)
  if (!hasError) {
    return 'ready'
  }

  const hasData = pointsCalculations.some(calculation => calculation?.data !== undefined)
  return hasData ? 'partial' : 'unavailable'
}

/**
 * `useGroupedLoyaltyEarnTotals` (owned by `src/loyalty`) returns its totals as a plain array,
 * positionally aligned with the `groupKeys` array passed in -- deliberately left untouched here
 * rather than changed to a keyed return shape, since it's shared/loyalty-owned code. This zips
 * that array with the same `groupKeys` locally so callers in this tripPlanner-owned module can
 * look totals up by stable rate/package key instead of by array position.
 */
function zipLoyaltyEarnTotalsByGroupKey(
  groupKeys: Array<OfferPageRoomPackageLoyaltyGroupKey>,
  totals: Array<LoyaltyEarnTotals | undefined>,
): Record<string, LoyaltyEarnTotals | undefined> {
  const result: Record<string, LoyaltyEarnTotals | undefined> = {}
  groupKeys.forEach((groupKey, index) => {
    result[groupKey] = totals[index]
  })
  return result
}

export interface OfferPageRoomPackageLoyaltyEarnTotalsResult {
  totalsByGroupKey: Record<string, LoyaltyEarnTotals | undefined>
  status: OfferPageLoyaltyEarnCalculationStatus | undefined
}

export default function useOfferPageRoomPackageLoyaltyEarnTotals(
  params: UseOfferPageRoomPackageLoyaltyEarnTotalsParams,
): OfferPageRoomPackageLoyaltyEarnTotalsResult {
  const {
    offerPageState,
    currentRegionCode,
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    propertyIsUltraLux,
    effectiveFlightPrice,
    shouldUseCharterFallbackPricing,
    isRewardSelected,
    flightLoyaltyEarnKey,
  } = params
  const {
    groupKeys,
    calculationRequests,
  } = useMemo(() => buildCalculationRequests({
    offerPageState,
    currentRegionCode,
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    propertyIsUltraLux,
    effectiveFlightPrice,
    shouldUseCharterFallbackPricing,
    isRewardSelected,
    flightLoyaltyEarnKey,
  }), [
    bedbankPackageViews,
    currentRegionCode,
    effectiveFlightPrice,
    shouldUseCharterFallbackPricing,
    isRewardSelected,
    flightLoyaltyEarnKey,
    hotelPackageViews,
    offerPageState,
    propertyIsUltraLux,
    propertyRoomRateViews,
  ])

  const totals = useGroupedLoyaltyEarnTotals(groupKeys, calculationRequests)
  // A second read of the same underlying calculations, purely to derive an aggregate status --
  // deliberately not a second *request*: `submitLoyaltyPointsEarnCalculationBatchRequest` dedupes
  // by key against whatever's already `fetching`/settled in the store, and this call passes the
  // exact same request array `useGroupedLoyaltyEarnTotals` above already dispatched, so it hits
  // that same in-flight/cached state rather than firing a second API call.
  const requestsOnly = useMemo(() => calculationRequests.map(requestInfo => requestInfo.request), [calculationRequests])
  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(requestsOnly, {
    skipAll: requestsOnly.length === 0,
    useBatch: true,
  })
  const status = useMemo(() => getOfferPageLoyaltyEarnCalculationStatus(pointsCalculations), [pointsCalculations])

  return useMemo(
    () => ({
      totalsByGroupKey: zipLoyaltyEarnTotalsByGroupKey(groupKeys, totals),
      status,
    }),
    [groupKeys, totals, status],
  )
}
